import frenchRules from '../seed/fr.json';
import spanishRules from '../seed/es.json';
import { normalizeWord } from '../src/domain/language-pack.js';
import './styles.css';
import './redesign.css';
import { createSavedGames } from './saved-games.js';
import { createRequestId } from './request-id.js';
import russianRules from '../seed/ru.json';
import { initializeSession, requestApi } from './api.js';
import { copyInvitationLink } from './copy-invitation-link.js';
import { shareInvitationLink } from './share-invitation-link.js';
import { applyThemeAssets } from './assets.js';
import { playCorrectWordSound, playIncorrectWordSound, playLetterSelectionSound, playLetterRemovalSound, playRefreshSound, playNextRoundSound, setupBackgroundMusic } from './audio.js';
import { LetterBoard, displayedSymbol } from './board.js';
import { applyIcons, createIcon } from './icons.js';
import { selectWordTiles } from './word-selection.js';
import { showTestRewardedAd } from './test-rewarded-ad.js';
import { setupPlayerUi, refreshPlayer, updatePlayerActions, openPlayerProfile } from './player-ui.js';
import { formatNumber, getLocale, setLocale, translate } from './translations.js';

const element = id => document.getElementById(id);
let currentGame;
let jokerEnabled = localStorage.getItem('wordcraft.jokerEnabled') === 'true';
let jokerChoices = new Map();
const jokerSetting = document.createElement('div');
jokerSetting.className = 'settings-row';
const jokerSettingLabel = document.createElement('label');
jokerSettingLabel.htmlFor = 'joker-mode-toggle';
jokerSettingLabel.dataset.i18n = 'playWithJokers';
const jokerToggle = document.createElement('input');
jokerToggle.id = 'joker-mode-toggle';
jokerToggle.type = 'checkbox';
jokerToggle.className = 'switch-input';
jokerToggle.checked = jokerEnabled;
jokerSetting.append(createIcon('crown'), jokerSettingLabel, jokerToggle);
element('round-assistance-toggle').closest('.settings-row').before(jokerSetting);
const jokerHint = document.createElement('p');
jokerHint.className = 'subtle-text joker-setting-hint';
jokerHint.dataset.i18n = 'jokerModeHint';
jokerSetting.after(jokerHint);
jokerToggle.addEventListener('change', () => {
  const enabled = jokerToggle.checked;
  performAction(async () => {
    try {
      if (currentGame?.mode === 'practice' && currentGame.status === 'active') {
        renderGame(await requestApi(`/games/${currentGame.id}/joker`, { method: 'PATCH', body: { enabled } }));
      }
      jokerEnabled = enabled;
      localStorage.setItem('wordcraft.jokerEnabled', String(enabled));
    } finally { jokerToggle.checked = jokerEnabled; }
  });
});
let friendTableId;
let friendPolling = false;
let activeFriendTableId;
let lastFriendHeartbeat = 0;
let lastPeerPresence;
let presentedFriendResultId;
const leaveFriendButton = document.createElement('button');
leaveFriendButton.id = 'leave-friend-game';
leaveFriendButton.type = 'button';
leaveFriendButton.className = 'secondary-button full-width';
leaveFriendButton.dataset.i18n = 'leaveFriendGame';
leaveFriendButton.hidden = true;
element('resume-game').after(leaveFriendButton);

async function leaveFriendTable() {
  const id = activeFriendTableId;
  activeFriendTableId = undefined;
  if (!id) return;
  await requestApi(`/friend-tables/${encodeURIComponent(id)}/presence`, { method: 'POST', body: { state: 'left' }, keepalive: true });
}

function showFriendResult(table) {
  if (document.body.dataset.view !== 'game') return;
  const dialog = element('friend-result-dialog');
  if (!dialog.open) openDialog('friend-result-dialog');
  presentedFriendResultId = table.id;
  presentedResultRoundId = currentGame.round.id;
}

function synchronizeFriendDialogs(table) {
  if (!table || document.body.dataset.view !== 'game') return;
  if (['completed', 'cancelled'].includes(table.status)) {
    if (presentedFriendResultId !== table.id) showFriendResult(table);
    return;
  }
  const peer = table.players.find(player => player.seat !== table.ownSeat);
  const key = `${table.id}:${peer?.presence ?? 'online'}`;
  if (lastPeerPresence === key) return;
  lastPeerPresence = key;
  if (peer?.presence === 'online') {
    element('friend-presence-dialog').close();
  } else if (peer) {
    element('friend-presence-title').textContent = translate(peer.presence === 'left' ? 'opponentLeft' : 'opponentDisconnected');
    element('friend-presence-message').textContent = translate('friendReconnectHint');
    openDialog('friend-presence-dialog');
  }
}

leaveFriendButton.addEventListener('click', () => performAction(async () => {
  await leaveFriendTable();
  closeDialogs();
  showHome();
}));
element('friend-result-home').addEventListener('click', () => { closeDialogs(); showHome(); });
window.addEventListener('pagehide', () => {
  if (activeFriendTableId) void requestApi(`/friend-tables/${encodeURIComponent(activeFriendTableId)}/presence`, {
    method: 'POST', body: { state: 'departing' }, keepalive: true,
  }).catch(() => {});
});
element('friend-dialog').addEventListener('close', () => {
  if (document.body.dataset.view !== 'game' || currentGame?.friendTable?.id !== friendTableId) {
    void leaveFriendTable().catch(error => { actionError.textContent = translate(error.code ?? 'network_error'); });
  }
});
const friendButton = document.createElement('button');
friendButton.id = 'play-friend';
friendButton.type = 'button';
friendButton.className = 'secondary-button full-width';
friendButton.dataset.i18n = 'playFriend';
element('new-game').after(friendButton);
const friendScore = document.createElement('p');
friendScore.id = 'friend-score';
friendScore.className = 'friend-score';
friendScore.setAttribute('role', 'status');
element('round-number').closest('.game-status').after(friendScore);
const friendSummary = friendScore.cloneNode();
friendSummary.id = 'friend-summary';
friendSummary.className = 'friend-summary-text';
element('round-summary-content').prepend(friendSummary);
const nextRoundNotice = document.createElement('p');
nextRoundNotice.id = 'next-round-status';
nextRoundNotice.className = 'subtle-text';
nextRoundNotice.setAttribute('role', 'status');
nextRoundNotice.setAttribute('aria-live', 'polite');
nextRoundNotice.hidden = true;
element('round-result').append(nextRoundNotice);
const actionError = document.createElement('p');
actionError.className = 'action-error';
actionError.setAttribute('role', 'alert');
document.body.append(actionError);

function renderFriendScore(table) {
  nextRoundNotice.hidden = true;
  friendScore.hidden = !table;
  friendSummary.hidden = !table;
  if (!table) return;
  const own = table.players.find(player => player.seat === table.ownSeat);
  const peer = table.players.find(player => player.seat !== table.ownSeat);
  const waitingForNext = table.status === 'active' && currentGame?.round.status === 'answered'
    && own.readyRound >= currentGame.round.number;
  if (table.status === 'active' && currentGame?.round.status === 'answered') {
    nextRoundNotice.textContent = translate(waitingForNext
      ? peer?.completedRounds < currentGame.round.number ? 'opponentHasNotSubmitted' : 'waitingOpponentNext'
      : currentGame.status === 'completed' ? 'waitingFriendResult' : 'waitingFriendRound');
    nextRoundNotice.hidden = false;
  }
  let summary = translate('friendScore', { yours: formatNumber(own.points), theirs: formatNumber(peer?.points ?? 0),
    yourTime: formatNumber(own.elapsedMs / 1000), theirTime: formatNumber((peer?.elapsedMs ?? 0) / 1000) });
  const scoreSummary = summary;
  if (table.status === 'cancelled') summary += ` · ${translate('friend_game_cancelled')}`;
  else if (table.status === 'completed') summary += ` · ${translate(table.winnerSeat === null ? 'friendDraw' : table.winnerSeat === table.ownSeat ? 'friendWon' : 'friendLost')}`;
  else if (peer?.presence === 'left' || peer?.presence === 'disconnected') summary += ` · ${translate(peer.presence === 'left' ? 'opponentLeft' : 'opponentDisconnected')}`;
  else if (!nextRoundNotice.hidden) summary += ` · ${nextRoundNotice.textContent}`;
  const playerSummaries = scoreSummary.split(' | ');
  friendSummary.replaceChildren();
  const summaryScores = document.createElement('span');
  summaryScores.className = 'friend-summary-scores';
  playerSummaries.forEach((text, index) => {
    if (index > 0) {
      const separator = document.createElement('span');
      separator.className = 'friend-summary-separator';
      separator.textContent = ' | ';
      summaryScores.append(separator);
    }
    const player = document.createElement('span');
    player.className = 'friend-summary-player';
    const separatorAt = text.indexOf(' · ');
    const score = document.createElement('strong');
    score.textContent = separatorAt < 0 ? text : text.slice(0, separatorAt);
    player.append(score);
    if (separatorAt >= 0) {
      const time = document.createElement('span');
      time.className = 'friend-summary-time';
      time.textContent = text.slice(separatorAt + ' · '.length);
      player.append(time);
    }
    summaryScores.append(player);
  });
  friendSummary.append(summaryScores);
  if (summary.length > scoreSummary.length) {
    const status = document.createElement('span');
    status.className = 'friend-summary-status';
    status.textContent = summary.slice(scoreSummary.length);
    friendSummary.append(status);
  }
  friendScore.setAttribute('aria-label', summary);
  const scorePlayers = [own, peer ?? { points: 0, elapsedMs: 0 }].map((player, index) => {
    const group = document.createElement('span');
    group.className = 'friend-score-player';
    const name = document.createElement('span');
    name.className = 'friend-score-name';
    name.textContent = translate(index === 0 ? 'friendYou' : 'friendOpponent');
    const points = document.createElement('strong');
    points.className = 'friend-score-points';
    points.textContent = formatNumber(player.points);
    points.title = translate('points');
    const dot = document.createElement('span');
    dot.className = 'friend-score-dot';
    dot.textContent = '·';
    dot.setAttribute('aria-hidden', 'true');
    const time = document.createElement('span');
    time.className = 'friend-score-time';
    time.textContent = `${formatNumber(player.elapsedMs / 1000)}${translate('friendSeconds')}`;
    group.append(name, points, dot, time);
    return group;
  });
  const divider = document.createElement('span');
  divider.className = 'friend-score-divider';
  divider.setAttribute('aria-hidden', 'true');
  friendScore.replaceChildren(scorePlayers[0], divider, scorePlayers[1]);
  if (['completed', 'cancelled'].includes(table.status)) {
    element('friend-result-title').textContent = translate(table.status === 'cancelled' ? 'friend_game_cancelled' : table.winnerSeat === null ? 'friendDraw' : table.winnerSeat === table.ownSeat ? 'friendWon' : 'friendLost');
    element('friend-result-description').textContent = translate(table.status === 'cancelled' ? 'friendCancelledHint' : 'friendResultRules');
    const ranked = [...table.players].sort((a, b) => b.points - a.points || a.elapsedMs - b.elapsedMs);
    const leadingSeat = table.winnerSeat ?? (ranked.length === 2 && (ranked[0].points !== ranked[1].points || ranked[0].elapsedMs !== ranked[1].elapsedMs) ? ranked[0].seat : null);
    const ordered = [...table.players].sort((a, b) => Number(b.seat === table.ownSeat) - Number(a.seat === table.ownSeat));
    element('friend-result-players').replaceChildren(...ordered.map(player => {
      const card = document.createElement('section');
      card.className = 'friend-player-card';
      card.classList.toggle('friend-player-leading', player.seat === leadingSeat);
      const heading = document.createElement('div');
      heading.className = 'friend-player-heading';
      const badge = document.createElement('span');
      badge.className = 'friend-player-badge';
      badge.append(createIcon(player.seat === leadingSeat ? 'crown' : 'player'));
      const name = document.createElement('h3');
      name.textContent = translate(player.seat === table.ownSeat ? 'friendYou' : 'friendOpponent');
      heading.append(badge, name);
      const stats = document.createElement('dl');
      stats.className = 'friend-player-stats';
      const values = [['friendPoints', formatNumber(player.points)], ['friendTime', new Intl.NumberFormat(getLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(player.elapsedMs / 1000)], ['completedRounds', formatNumber(player.completedRounds)]];
      for (const [key, value] of values) {
        const metric = document.createElement('div');
        const label = document.createElement('dt');
        label.textContent = translate(key);
        const number = document.createElement('dd');
        number.textContent = value;
        if (key === 'friendTime') { number.className = 'friend-time-value'; const unit = document.createElement('small'); unit.textContent = translate('friendSeconds'); number.append(unit); }
        metric.append(label, number);
        stats.append(metric);
      }
      card.append(heading, stats);
      return card;
    }));
  }
}

function enterFriendTable(table) {
  friendTableId = table.id;
  if (['completed', 'cancelled'].includes(table.status)) {
    presentedFriendResultId = undefined;
    presentedResultRoundId = undefined;
  }
  activeFriendTableId = table.id;
  lastFriendHeartbeat = 0;
  lastPeerPresence = undefined;
  const url = new URL(location.href);
  url.searchParams.set('table', table.id);
  history.replaceState(null, '', url);
  if (table.game) {
    setLocale(table.game.language.code);
    closeDialogs();
    renderGame(table.game);
  } else {
    if (table.status === 'cancelled') {
      activeFriendTableId = undefined;
      renderFriendScore(table);
      openDialog('friend-result-dialog');
      return;
    }
    element('friend-link').value = url.href;
    element('friend-copy-status').textContent = '';
    openDialog('friend-dialog');
  }
}

friendButton.addEventListener('click', () => performAction(async () => {
  await leaveFriendTable();
  enterFriendTable(await requestApi('/friend-tables', { method: 'POST', body: { languageCode: getLocale(), jokerEnabled } }));
}));
function showFriendCopyStatus(copied) {
  const status = element('friend-copy-status');
  status.dataset.state = copied ? 'success' : 'manual';
  const message = document.createElement('span');
  message.textContent = translate(copied ? 'friendLinkCopied' : 'copyFriendManually');
  status.replaceChildren(createIcon(copied ? 'check' : 'clipboard'), message);
}

element('copy-friend-link').addEventListener('click', async () => {
  const copied = await copyInvitationLink(element('friend-link'));
  showFriendCopyStatus(copied);
});
element('share-friend-link').hidden = typeof navigator.share !== 'function';
element('share-friend-link').addEventListener('click', async () => {
  const button = element('share-friend-link');
  const status = element('friend-copy-status');
  status.textContent = '';
  button.disabled = true;
  try {
    const result = await shareInvitationLink(element('friend-link').value, translate('playFriend'));
    if (result === 'unavailable') {
      const copied = await copyInvitationLink(element('friend-link'));
      showFriendCopyStatus(copied);
    }
  } finally { button.disabled = false; }
});

setInterval(async () => {
  if (!friendTableId || !activeFriendTableId || pendingRequest || friendPolling) return;
  const waiting = element('friend-dialog').open;
  if (!waiting && (document.body.dataset.view !== 'game' || currentGame?.mode !== 'friend')) return;
  const id = friendTableId;
  friendPolling = true;
  try {
    if (Date.now() - lastFriendHeartbeat >= 5000) {
      await requestApi(`/friend-tables/${encodeURIComponent(id)}/presence`, { method: 'POST', body: { state: 'online' } });
      lastFriendHeartbeat = Date.now();
    }
    const table = await requestApi(`/friend-tables/${encodeURIComponent(id)}`);
    if (id !== friendTableId || id !== activeFriendTableId || pendingRequest) return;
    if (waiting && (table.game || table.status === 'cancelled')) enterFriendTable(table);
    else if (table.game && currentGame?.id === table.game.id) {
      const next = table.game;
      if (next.round.id !== currentGame.round.id || next.round.status !== currentGame.round.status
        || next.round.waitingForFriend !== currentGame.round.waitingForFriend
        || JSON.stringify(next.friendTable) !== JSON.stringify(currentGame.friendTable)) renderGame(next);
    }
    actionError.textContent = '';
  } catch (error) { actionError.textContent = translate(error.code ?? 'network_error'); }
  finally { friendPolling = false; }
}, 1000);
const savedGames = createSavedGames(localStorage);
const hasLanguageGame = () => currentGame?.language.code === getLocale();
let letterBoard;
let pendingRequest = false;
let selectedTileIds = new Set();
let currentSelection;
let renderedRoundId;
let availableLanguages = [];
let assistanceMode = 'unavailable';
let isAdmin = false;
let adminSolutionStatuses;
let roundAssistanceEnabled = localStorage.getItem('wordcraft.roundAssistance') !== 'false';
let assistanceContext;
let letterChangeInProgress = false;
let renderedBoardRevision;
let timerRoundId;
let timerDeadline;
let timerRefreshRetryAt = 0;
const mobileViewport = window.matchMedia('(max-width: 760px)');
const clearWordPosition = document.createComment('Desktop clear button position');
element('clear-word').before(clearWordPosition);

function synchronizeMobileWordControls() {
  const button = element('clear-word');
  if (mobileViewport.matches) document.querySelector('.word-heading').insertBefore(button, document.querySelector('.word-points'));
  else clearWordPosition.after(button);
}
const resultPopupNodes = ['round-result', 'suggestion-panel', 'next-round'].map(id => element(id));
resultPopupNodes.push(document.querySelector('.game-sidebar'));
const resultPopupPositions = resultPopupNodes.map(node => {
  const placeholder = document.createComment('Round result position');
  node.before(placeholder);
  return { node, placeholder };
});
let presentedResultRoundId;

function synchronizeResultPopup(open = false) {
  if (['completed', 'cancelled'].includes(currentGame?.friendTable?.status)) {
    element('show-round-summary').hidden = false;
    if (open) showFriendResult(currentGame.friendTable);
    return;
  }
  const dialog = element('round-summary-dialog');
  const waitingForFriendFinish = currentGame?.mode === 'friend' && currentGame.status === 'completed'
    && currentGame.friendTable?.status === 'active';
  if (waitingForFriendFinish) {
    if (dialog.open) dialog.close();
    for (const { node, placeholder } of resultPopupPositions) placeholder.after(node);
    element('show-round-summary').hidden = true;
    return;
  }
  const answered = currentGame?.round.status === 'answered';
  const completed = currentGame?.status === 'completed';
  const inGame = document.body.dataset.view === 'game';
  element('show-round-summary').hidden = !(mobileViewport.matches || completed) || !(answered || completed);
  if (mobileViewport.matches || completed) {
    for (const node of resultPopupNodes) {
      const host = element(node.id === 'next-round' ? 'round-summary-footer' : 'round-summary-content');
      if (node.parentElement !== host) host.appendChild(node);
    }
    if ((answered || completed) && inGame && open && !dialog.open) {
      setSolutionsExpanded(false);
      openDialog('round-summary-dialog');
      presentedResultRoundId = currentGame.round.id;
    } else if ((!(answered || completed) || !inGame) && dialog.open) dialog.close();
  } else {
    if (dialog.open) dialog.close();
    for (const { node, placeholder } of resultPopupPositions) placeholder.after(node);
  }
}

mobileViewport.addEventListener('change', () => {
  synchronizeMobileWordControls();
  synchronizeResultPopup(true);
});
element('show-round-summary').addEventListener('click', () => synchronizeResultPopup(true));

function showRewardAd(purpose) {
  const timeBonus = purpose === 'time';
  element('test-ad-title').textContent = translate(timeBonus ? 'timeBonusAdTitle' : 'testAdTitle');
  element('test-ad-description').textContent = translate(timeBonus ? 'timeBonusAdDescription' : 'testAdDescription');
  element('test-ad-cancel').textContent = translate(timeBonus ? 'timeBonusAdCancel' : 'testAdCancel');
  element('test-ad-progress').setAttribute('aria-label', translate(timeBonus ? 'timeBonusAdTitle' : 'testAdTitle'));
  return showTestRewardedAd({
    dialog: element('test-ad-dialog'), progress: element('test-ad-progress'), status: element('test-ad-status'),
    formatStatus: seconds => translate('testAdCountdown', { seconds: formatNumber(seconds) }),
  });
}

function openAssistance(kind, tile) {
  if (kind === 'letter' && currentGame?.jokerEnabled && tile?.symbol === 'JK') return;
  if (!roundAssistanceEnabled || assistanceMode === 'unavailable' || pendingRequest || !currentGame || currentGame.mode !== 'practice' || currentGame.round.status !== 'active' || currentGame.round.assistance) return;
  performAction(async () => {
    const gameId = currentGame.id;
    const roundId = currentGame.round.id;
    const options = await requestApi(`/games/${gameId}/rounds/${roundId}/assistance`);
    if (!options.available) return;
    if (kind === 'letter') renderGame(await requestApi(`/games/${gameId}/rounds/${roundId}/assistance/pause`, { method: 'POST', body: {} }));
    assistanceContext = { gameId, roundId, kind, tileId: tile?.id, options, requestId: createRequestId() };
    element('assistance-error').textContent = '';
    element('assistance-title').textContent = translate(kind === 'letter' ? 'changeLetter' : 'changeBoard');
    element('assistance-hint').textContent = translate(assistanceMode === 'test' ? 'assistanceTestHint' : 'assistanceHint');
    element('assistance-description').textContent = translate(kind === 'letter' ? currentGame.jokerEnabled ? 'confirmJokerChange' : 'confirmLetterChange' : 'changeBoardHint', { symbol: tile ? displayedSymbol(tile.symbol, currentGame.language.code) : '' });
    element('assistance-letter-fields').hidden = true;
    element('apply-assistance').textContent = translate(kind === 'letter' ? assistanceMode === 'test' ? 'confirmLetterButton' : 'confirmLetterNow' : assistanceMode === 'test' ? 'applyTestAssistance' : 'applyAssistance');
    openDialog('assistance-dialog');
  });
}
element('choose-board-help').addEventListener('click', () => openAssistance('board'));
element('apply-assistance').addEventListener('click', () => {
  if (!assistanceContext) return;
  element('assistance-error').textContent = '';
  const { gameId, roundId, kind, tileId, options, requestId } = assistanceContext;
  const body = { kind, requestId, boardRevision: options.boardRevision };
  if (kind === 'letter') body.tileId = tileId;
  const oldTiles = currentGame.round.tiles;
  const retained = currentSelection.selectedTiles.filter(tile => tile.id !== tileId);
  const draft = retained.map(tile => tile.symbol).join('') + currentSelection.remainder;
  let simulationCompleted = false;
  performAction(async () => {
    if (kind === 'letter') letterChangeInProgress = true;
    try {
      if (kind === 'letter' && !simulationCompleted) {
        if (assistanceMode === 'unavailable') return;
        if (assistanceMode === 'test') {
          closeDialogs();
          simulationCompleted = await showRewardAd('letter');
          if (!simulationCompleted) {
            renderGame(await requestApi(`/games/${gameId}/rounds/${roundId}/assistance/resume`, { method: 'POST', body: {} }));
            assistanceContext = undefined;
            return;
          }
        }
      }
      const game = await requestApi(`/games/${gameId}/rounds/${roundId}/assistance`, { method: 'POST', body });
      closeDialogs();
      renderGame(game);
      if (kind === 'letter' && game.round.id === roundId && game.round.status === 'active') {
        element('word-input').value = draft;
        selectedTileIds = new Set(retained.map(tile => game.round.tiles[oldTiles.findIndex(old => old.id === tile.id)].id));
        updateSelection();
      }
      if (game.round.id === roundId && game.round.boardRevision > options.boardRevision) playRefreshSound();
    } catch (error) {
      if (kind === 'letter' && currentGame?.round.timerPaused) {
        try { renderGame(await requestApi(`/games/${gameId}/rounds/${roundId}/assistance/resume`, { method: 'POST', body: {} })); }
        catch { /* A fresh page load will resume the saved timer. */ }
      }
      throw error;
    } finally {
      letterChangeInProgress = false;
    }
  });
});
element('assistance-dialog').addEventListener('close', () => {
  if (letterChangeInProgress || assistanceContext?.kind !== 'letter' || !currentGame?.round.timerPaused) return;
  const { gameId, roundId } = assistanceContext;
  assistanceContext = undefined;
  performAction(async () => renderGame(await requestApi(`/games/${gameId}/rounds/${roundId}/assistance/resume`, { method: 'POST', body: {} })));
});
element('add-time').addEventListener('click', () => {
  if (!currentGame || currentGame.mode !== 'practice' || currentGame.round.status !== 'active' || currentGame.round.timerPaused) return;
  const gameId = currentGame.id;
  const roundId = currentGame.round.id;
  const requestId = createRequestId();
  let adCompleted = false;
  performAction(async () => {
    try {
      if (!adCompleted) {
        renderGame(await requestApi(`/games/${gameId}/rounds/${roundId}/time-bonus/pause`, { method: 'POST', body: {} }));
        adCompleted = await showRewardAd('time');
        if (!adCompleted) {
          renderGame(await requestApi(`/games/${gameId}/rounds/${roundId}/time-bonus/resume`, { method: 'POST', body: {} }));
          return;
        }
      }
      renderGame(await requestApi(`/games/${gameId}/rounds/${roundId}/time-bonus`, { method: 'POST', body: { requestId } }));
    } catch (error) {
      try { renderGame(await requestApi(`/games/${gameId}/rounds/${roundId}/time-bonus/resume`, { method: 'POST', body: {} })); }
      catch { /* A fresh page load resumes a saved pause. */ }
      throw error;
    }
  });
});
const selectedReportWords = new Set();
let reportRoundId;
let adminDoubleValidatedWords = new Set();

function applyAdminSolutionFilters() {
  const expanded = element('solutions-card').classList.contains('solutions-expanded');
  const hideVerified = expanded && isAdmin && adminSolutionStatuses && element('hide-verified-solutions').checked;
  const hidePending = expanded && isAdmin && adminSolutionStatuses && element('hide-pending-solutions').checked;
  let visibleCount = 0;
  for (const item of element('solutions-list').children) {
    const status = adminSolutionStatuses?.[item.dataset.word] ?? 'verified';
    item.hidden = Boolean((hideVerified && adminDoubleValidatedWords.has(item.dataset.word)) || (hidePending && status === 'pending'));
    if (item.hidden) {
      selectedReportWords.delete(item.dataset.word);
      const checkbox = item.querySelector('.solution-checkbox');
      checkbox.checked = checkbox.dataset.reported === 'true';
    } else visibleCount++;
  }
  element('solutions-filter-empty').hidden = !expanded || !isAdmin || !adminSolutionStatuses || visibleCount > 0;
  synchronizeControls();
}

async function loadAdminSolutionStatuses(gameId, roundId) {
  try {
    const result = await requestApi(`/admin/games/${encodeURIComponent(gameId)}/rounds/${encodeURIComponent(roundId)}/solution-report-statuses`);
    if (currentGame?.id !== gameId || currentGame.round.id !== roundId || !element('solutions-card').classList.contains('solutions-expanded')) return;
    adminSolutionStatuses = result.statuses;
    adminDoubleValidatedWords = new Set(result.doubleValidatedWords);
    for (const id of ['hide-verified-solutions', 'hide-pending-solutions']) element(id).disabled = false;
    applyAdminSolutionFilters();
  } catch (error) {
    if (currentGame?.id !== gameId || currentGame.round.id !== roundId || !element('solutions-card').classList.contains('solutions-expanded')) return;
    if (error.status === 401) isAdmin = false;
    element('admin-solution-filters').hidden = true;
    displayNotice(translate(error.code ?? 'network_error'), true);
  }
}

function setSolutionsExpanded(expanded) {
  element('solutions-card').classList.toggle('solutions-expanded', expanded);
  const button = element('expand-solutions');
  button.setAttribute('aria-expanded', String(expanded));
  button.dataset.i18nLabel = expanded ? 'collapseSolutions' : 'expandSolutions';
  button.setAttribute('aria-label', translate(button.dataset.i18nLabel));
  button.replaceChildren(createIcon(expanded ? 'collapse' : 'expand'));
  const filters = element('admin-solution-filters');
  filters.hidden = !expanded || !isAdmin;
  if (expanded && isAdmin && currentGame?.round.status === 'answered') {
    adminSolutionStatuses = undefined;
    adminDoubleValidatedWords = new Set();
    for (const id of ['hide-verified-solutions', 'hide-pending-solutions']) element(id).disabled = true;
    void loadAdminSolutionStatuses(currentGame.id, currentGame.round.id);
  } else {
    adminSolutionStatuses = undefined;
    adminDoubleValidatedWords = new Set();
    for (const id of ['hide-verified-solutions', 'hide-pending-solutions']) element(id).checked = false;
  }
  applyAdminSolutionFilters();
}

element('expand-solutions').addEventListener('click', () => setSolutionsExpanded(!element('solutions-card').classList.contains('solutions-expanded')));
for (const id of ['hide-verified-solutions', 'hide-pending-solutions']) element(id).addEventListener('change', applyAdminSolutionFilters);
element('select-all-solutions').addEventListener('change', event => {
  selectedReportWords.clear();
  if (event.target.checked) {
    for (const checkbox of element('solutions-list').querySelectorAll('li:not([hidden]) .solution-checkbox'))
      if (checkbox.dataset.reported !== 'true') selectedReportWords.add(checkbox.value);
  }
  for (const checkbox of element('solutions-list').querySelectorAll('.solution-checkbox')) {
    checkbox.checked = checkbox.dataset.reported === 'true' || selectedReportWords.has(checkbox.value);
  }
  synchronizeControls();
});
document.addEventListener('keydown', event => { if (event.key === 'Escape') setSolutionsExpanded(false); });
element('report-selected-words').addEventListener('click', () => {
  const game = currentGame;
  const words = [...selectedReportWords];
  performAction(async () => {
    await requestApi(`/games/${game.id}/rounds/${game.round.id}/word-reports/selection`,
      { method: 'POST', body: { selectedWords: words } });
    const reported = new Set([...(game.round.reportedWords ?? []), ...words]);
    if (adminSolutionStatuses) {
      for (const solution of game.round.solutions) {
        if (words.includes(solution.word)) adminSolutionStatuses[solution.word] = 'pending';
        else if (!reported.has(solution.word)) adminDoubleValidatedWords.add(solution.word);
      }
    }
    game.round.reportedWords = [...reported];
    selectedReportWords.clear();
    renderGame(game);
  });
});

async function performAction(action) {
  if (pendingRequest) return;
  pendingRequest = true;
  actionError.textContent = '';
  synchronizeControls();
  try { await action(); }
  catch (error) {
    actionError.textContent = translate(error.code ?? 'internal_error');
    if (element('assistance-dialog').open) element('assistance-error').textContent = translate(error.code ?? 'internal_error');
    if (['board_changed', 'assistance_used', 'friend_game_cancelled'].includes(error.code) && currentGame) {
      try { closeDialogs(); renderGame(await requestApi(`/games/${currentGame.id}`)); } catch { /* Keep the original error and current local board. */ }
    }
    console.warn('Game action failed:', error.code ?? 'internal_error');
  }
  finally { pendingRequest = false; synchronizeControls(); }
}

function synchronizeControls() {
  const answered = currentGame?.round.status === 'answered';
  const completed = ['completed', 'cancelled'].includes(currentGame?.status);
  const hasWord = Boolean(element('word-input').value.trim());
  const selectedLanguageAvailable = availableLanguages.some(language => language.code === getLocale());
  jokerToggle.disabled = pendingRequest || Boolean(document.body.dataset.view === 'game' && currentGame?.status === 'active' && currentGame.mode !== 'practice');
  for (const flag of document.querySelectorAll('[data-locale]')) flag.disabled = pendingRequest || !availableLanguages.some(language => language.code === flag.dataset.locale);
  element('new-game').disabled = pendingRequest || !selectedLanguageAvailable;
  element('daily-game').disabled = pendingRequest || !selectedLanguageAvailable;
  leaveFriendButton.hidden = currentGame?.mode !== 'friend' || ['completed', 'cancelled'].includes(currentGame.friendTable.status);
  leaveFriendButton.disabled = pendingRequest;
  friendButton.disabled = pendingRequest || !selectedLanguageAvailable;
  element('continue-game').disabled = pendingRequest || !hasLanguageGame();
  element('resume-game').hidden = !hasLanguageGame();
  element('confirm-new-game').disabled = pendingRequest || !selectedLanguageAvailable;
  element('menu-new-game').disabled = pendingRequest || !selectedLanguageAvailable;
  for (const id of ['word-input', 'backspace-word', 'focus-keyboard']) element(id).disabled = pendingRequest || !currentGame || answered || completed;
  element('clear-word').disabled = pendingRequest || !currentGame || answered || completed || !currentSelection?.selectedTiles.length;
  element('submit-answer').disabled = pendingRequest || !currentGame || !hasWord || completed;
  element('summary-new-game').disabled = pendingRequest;
  const alreadyReady = Boolean(currentGame?.friendTable?.players.find(player => player.seat === currentGame.friendTable.ownSeat)?.readyRound >= currentGame?.round.number);
  const nextRoundReady = !pendingRequest && !completed && answered && !alreadyReady
    && currentGame?.mode === 'friend' && currentGame.friendTable.status === 'active'
    && currentGame.round.waitingForFriend === false;
  element('next-round').disabled = pendingRequest || completed || alreadyReady;
  element('next-round').classList.toggle('next-round-ready', nextRoundReady);
  element('show-round-summary').classList.toggle('next-round-ready', nextRoundReady);
  element('suggest-word').disabled = pendingRequest;
  const assistanceVisible = assistanceMode !== 'unavailable' && roundAssistanceEnabled && (!currentGame || currentGame.mode === 'practice');
  element('round-assistance-toggle').disabled = assistanceMode === 'unavailable';
  element('assistance-controls').hidden = !assistanceVisible;
  const helpDisabled = pendingRequest || !currentGame || answered || completed || Boolean(currentGame?.round.assistance);
  for (const button of element('accessible-tiles').querySelectorAll('.tile-change-button')) {
    button.disabled = helpDisabled;
    button.hidden = Boolean(currentGame?.jokerEnabled && button.dataset.joker === 'true');
  }
  element('choose-board-help').disabled = helpDisabled;
  element('add-time').disabled = pendingRequest || !currentGame || currentGame.round.status !== 'active' || currentGame.round.timerPaused;
  element('apply-assistance').disabled = pendingRequest || !assistanceContext;
  element('assistance-old-letter').disabled = pendingRequest;
  element('assistance-new-letter').disabled = pendingRequest;
  for (const checkbox of element('solutions-list').querySelectorAll('input[type="checkbox"]')) checkbox.disabled = pendingRequest || checkbox.dataset.reported === 'true';
  element('report-selected-words').disabled = pendingRequest || !answered || !selectedReportWords.size;
  const reportableCount = [...element('solutions-list').querySelectorAll('li:not([hidden]) .solution-checkbox')]
    .filter(checkbox => checkbox.dataset.reported !== 'true').length;
  const selectAll = element('select-all-solutions');
  selectAll.disabled = pendingRequest || !answered || !reportableCount;
  selectAll.checked = reportableCount > 0 && selectedReportWords.size === reportableCount;
  selectAll.indeterminate = selectedReportWords.size > 0 && selectedReportWords.size < reportableCount;
  element('report-selected-words').textContent = translate('reportWord') + (selectedReportWords.size ? ` (${formatNumber(selectedReportWords.size)})` : '');
  element('board-loading').hidden = !pendingRequest;
  element('play-area').setAttribute('aria-busy', String(pendingRequest));
  for (const button of element('accessible-tiles').querySelectorAll('button[data-tile-id]')) {
    button.disabled = pendingRequest || answered || completed;
    button.setAttribute('aria-pressed', String(selectedTileIds.has(button.dataset.tileId)));
  }
  if (letterBoard) {
    letterBoard.disabled = Boolean(pendingRequest || answered || completed);
    letterBoard.setAssistance(assistanceVisible, !helpDisabled, Boolean(currentGame?.jokerEnabled));
    for (const view of letterBoard.tileViews) view.container.eventMode = letterBoard.disabled ? 'none' : 'static';
  }
}

function normalizedInput() {
  return normalizeWord(element('word-input').value, currentGame.language.code);
}

function updateSelection(playSound = false) {
  if (!currentGame) return;
  const previousTileIds = new Set(currentSelection?.selectedTiles.map(tile => tile.id));
  const previousRemainder = currentSelection?.remainder ?? '';
  currentSelection = selectWordTiles(currentGame.round.tiles, currentGame.rules.symbols, normalizedInput(), selectedTileIds, jokerChoices);
  for (const id of jokerChoices.keys()) if (!currentSelection.selectedIds.has(id)) jokerChoices.delete(id);
  if (playSound === true) {
    if (currentSelection.selectedTiles.some(tile => !previousTileIds.has(tile.id))) playLetterSelectionSound();
    else if ([...previousTileIds].some(id => !currentSelection.selectedIds.has(id)) || previousRemainder.length > currentSelection.remainder.length) playLetterRemovalSound();
  }
  selectedTileIds = currentSelection.selectedIds;
  element('selected-points').textContent = formatNumber(currentSelection.points);
  const previewHost = element('word-preview');
  const previousPreviews = new Map([...previewHost.querySelectorAll('[data-tile-id]:not(.word-tile-leave)')].map(node => [node.dataset.tileId, node]));
  const nextPreviews = [];
  for (const tile of currentSelection.selectedTiles) {
    const existing = previousPreviews.get(tile.id);
    if (existing && existing.dataset.symbol === tile.symbol) { nextPreviews.push(existing); continue; }
    const preview = document.createElement('span');
    preview.dataset.tileId = tile.id;
    preview.dataset.symbol = tile.symbol;
    preview.className = 'word-tile word-tile-enter';
    preview.classList.toggle('joker-word-tile', Boolean(tile.joker));
    if (tile.joker) preview.title = translate('jokerTileLabel', { symbol: tile.symbol, points: tile.points });
    preview.addEventListener('animationend', () => preview.classList.remove('word-tile-enter'), { once: true });
    preview.textContent = displayedSymbol(tile.symbol, currentGame.language.code);
    const points = document.createElement('small');
    points.textContent = String(tile.points);
    preview.appendChild(points);
    nextPreviews.push(preview);
  }
  if (currentSelection.remainder) {
    const invalid = document.createElement('span');
    invalid.className = 'invalid-word-fragment';
    invalid.textContent = currentSelection.remainder;
    nextPreviews.push(invalid);
  }
  const removed = [...previewHost.children].filter(child => !nextPreviews.includes(child) && !child.classList.contains('word-tile-leave'))
    .map(child => ({ child, left: child.offsetLeft, top: child.offsetTop, width: child.offsetWidth, height: child.offsetHeight }));
  for (const { child, left, top, width, height } of removed) {
    if (!child.classList.contains('word-tile')) { child.remove(); continue; }
    child.classList.remove('word-tile-enter');
    Object.assign(child.style, { position: 'absolute', left: `${left}px`, top: `${top}px`, width: `${width}px`, height: `${height}px`, pointerEvents: 'none' });
    child.classList.add('word-tile-leave');
    child.addEventListener('animationend', () => child.remove(), { once: true });
    // Clean up even if an animation is interrupted by a layout change.
    setTimeout(() => child.remove(), 250);
  }
  nextPreviews.forEach((node, index) => {
    const activeChildren = [...previewHost.children].filter(child => !child.classList.contains('word-tile-leave'));
    if (activeChildren[index] !== node) previewHost.insertBefore(node, activeChildren[index] ?? null);
  });
  element('word-preview').dir = currentGame.language.direction;
  element('word-preview').lang = currentGame.language.code;
  letterBoard?.setSelectedTiles(selectedTileIds);
  synchronizeControls();
}

function selectTile(tile) {
  if (pendingRequest || currentGame?.round.status !== 'active' || currentGame.status !== 'active') return;
  if (selectedTileIds.has(tile.id)) {
    element('word-input').value = currentSelection.selectedTiles
      .filter(selected => selected.id !== tile.id)
      .map(selected => selected.symbol).join('') + currentSelection.remainder;
    selectedTileIds.delete(tile.id);
    jokerChoices.delete(tile.id);
  } else {
    if (tile.symbol === 'JK') {
      const roundId = currentGame.round.id;
      element('joker-letter-options').replaceChildren(...currentGame.rules.symbols.map(symbol => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'secondary-button';
        button.textContent = displayedSymbol(symbol, currentGame.language.code);
        button.addEventListener('click', () => {
          if (currentGame.round.id !== roundId || currentGame.round.status !== 'active' || currentGame.status !== 'active') return closeDialogs();
          jokerChoices.set(tile.id, symbol);
          selectedTileIds.add(tile.id);
          element('word-input').value += symbol;
          closeDialogs();
          updateSelection(true);
        });
        return button;
      }));
      openDialog('joker-letter-dialog');
      return;
    }
    element('word-input').value += tile.symbol;
    selectedTileIds.add(tile.id);
  }
  updateSelection(true);
}

function removeLastTile() {
  const normalizedWord = normalizedInput();
  if (!currentSelection.remainder && currentSelection.selectedTiles.length) {
    const lastSymbol = currentSelection.selectedTiles.at(-1).symbol;
    selectedTileIds.delete(currentSelection.selectedTiles.at(-1).id);
    jokerChoices.delete(currentSelection.selectedTiles.at(-1).id);
    element('word-input').value = normalizedWord.slice(0, -lastSymbol.length);
  } else {
    const graphemes = Array.from(new Intl.Segmenter(currentGame.language.code, { granularity: 'grapheme' }).segment(element('word-input').value), entry => entry.segment);
    element('word-input').value = graphemes.slice(0, -1).join('');
  }
  updateSelection(true);
}

function renderAccessibleTiles() {
  element('accessible-tiles').replaceChildren();
  for (const tile of currentGame.round.tiles) {
    const button = document.createElement('button');
    button.type = 'button';
    button.dataset.tileId = tile.id;
    button.classList.toggle('joker-tile', tile.symbol === 'JK');
    button.setAttribute('aria-label', translate('tileLabel', { symbol: tile.symbol, points: tile.points }));
    const symbol = document.createElement('span');
    symbol.textContent = displayedSymbol(tile.symbol, currentGame.language.code);
    const points = document.createElement('small');
    points.textContent = String(tile.points);
    button.append(symbol, points);
    button.addEventListener('click', () => selectTile(tile));
    const wrapper = document.createElement('div');
    wrapper.className = 'fallback-tile';
    wrapper.appendChild(button);
    if (assistanceMode !== 'unavailable' && roundAssistanceEnabled && currentGame.mode === 'practice') {
      const change = document.createElement('button');
      change.type = 'button';
      change.className = 'tile-change-button';
      change.dataset.joker = String(tile.symbol === 'JK');
      change.setAttribute('aria-label', `${translate('changeLetter')}: ${displayedSymbol(tile.symbol, currentGame.language.code)}`);
      change.appendChild(createIcon('swap'));
      change.addEventListener('click', () => openAssistance('letter', tile));
      wrapper.appendChild(change);
    }
    element('accessible-tiles').appendChild(wrapper);
  }
}

async function restoreLanguageGame(gameId) {
  const languageCode = getLocale();
  try {
    let game = await requestApi(`/games/${encodeURIComponent(gameId)}`);
    savedGames.remember(game);
    if (game.language.code !== languageCode) return false;
    if (game.status === 'active' && game.mode === 'practice' && game.round.timerPaused) game = await requestApi(`/games/${encodeURIComponent(gameId)}/rounds/${game.round.id}/assistance/resume`, { method: 'POST', body: {} });
    renderGame(game);
    return true;
  } catch (error) {
    if (error.status !== 404) throw error;
    savedGames.forget(languageCode, gameId);
    if (currentGame?.id === gameId) currentGame = undefined;
    return false;
  }
}

async function resumeLanguageGame() {
  closeDialogs();
  const gameId = savedGames.get(getLocale());
  if (!gameId || !await restoreLanguageGame(gameId)) showHome();
}

function showHome() {
  document.body.dataset.view = 'home';
  synchronizeResultPopup();
  element('welcome-card').hidden = false;
  element('play-area').hidden = true;
  element('continue-game').hidden = !hasLanguageGame();
  if (hasLanguageGame()) element('saved-game-summary').textContent = `${currentGame.language.name} · ${translate('round')} ${formatNumber(currentGame.round.number)} · ${formatNumber(currentGame.statistics.totalPoints)} ${translate('points')}`;
  window.scrollTo(0, 0);
}

function updateRoundTimer() {
  if (!currentGame || timerDeadline === undefined) return;
  const active = currentGame.round.status === 'active';
  const remaining = active && !currentGame.round.timerPaused ? Math.max(0, timerDeadline - performance.now()) : currentGame.round.remainingMs;
  const seconds = Math.ceil(remaining / 1000);
  const timer = element('round-timer');
  timer.style.setProperty('--timer-progress', `${remaining / currentGame.round.durationMs * 100}%`);
  timer.dataset.expired = String(remaining === 0);
  timer.dataset.long = String(seconds >= 100);
  timer.setAttribute('aria-label', translate('secondsRemaining', { seconds: formatNumber(seconds) }));
  element('round-timer-seconds').textContent = formatNumber(seconds);
  if (active && remaining === 0) element('add-time').disabled = true;
  if (active && !currentGame.round.timerPaused && remaining === 0 && document.body.dataset.view === 'game' && !pendingRequest && performance.now() >= timerRefreshRetryAt) {
    timerRefreshRetryAt = performance.now() + 3000;
    const gameId = currentGame.id;
    performAction(async () => renderGame(await requestApi(`/games/${gameId}`)));
  }
}

setInterval(updateRoundTimer, 100);
document.addEventListener('visibilitychange', () => {
  if (!document.hidden && currentGame?.round.status === 'active' && !currentGame.round.timerPaused && document.body.dataset.view === 'game' && !pendingRequest) {
    performAction(async () => renderGame(await requestApi(`/games/${currentGame.id}`)));
  }
});

function showGame(resetScroll = true) {
  if (!hasLanguageGame()) return showHome();
  document.body.dataset.view = 'game';
  element('welcome-card').hidden = true;
  element('play-area').hidden = false;
  updateRoundTimer();
  letterBoard?.layout();
  if (resetScroll) window.scrollTo(0, 0);
  synchronizeResultPopup(resetScroll);
}

function renderGame(game) {
  savedGames.remember(game);
  if (game.language.code !== getLocale()) return;
  const newlyAnswered = currentGame?.id === game.id && currentGame.round.id === game.round.id
    && currentGame.round.status === 'active' && game.round.status === 'answered';
  if (game !== currentGame || timerRoundId !== game.round.id) {
    timerRoundId = game.round.id;
    timerDeadline = performance.now() + game.round.remainingMs;
    timerRefreshRetryAt = 0;
  }
  currentGame = game;
  if (game.mode !== 'daily') {
    jokerEnabled = game.jokerEnabled;
    jokerToggle.checked = jokerEnabled;
    if (game.mode === 'practice') localStorage.setItem('wordcraft.jokerEnabled', String(jokerEnabled));
  }
  if (game.friendTable) {
    friendTableId = game.friendTable.id;
    if (activeFriendTableId !== friendTableId) lastFriendHeartbeat = 0;
    activeFriendTableId = ['completed', 'cancelled'].includes(game.friendTable.status) ? undefined : friendTableId;
  }
  renderFriendScore(game.friendTable);
  element('add-time').hidden = game.mode !== 'practice' || game.round.status !== 'active';
  showGame(false);
  element('round-number').textContent = `${translate('round')} ${formatNumber(game.round.number)} / ${formatNumber(game.totalRounds)}`;
  element('playing-language').textContent = game.language.name + (game.mode === 'daily' ? ` · ${translate('dailyChallenge')}` : '');
  for (const target of document.querySelectorAll('[data-statistic]')) {
    const key = target.dataset.statistic;
    target.textContent = `${formatNumber(game.statistics[key])}${key === 'percentage' ? '%' : ''}`;
  }
  element('maximum-points').textContent = `/ ${formatNumber(game.statistics.maximumPossiblePoints)}`;
  element('score-progress').value = game.statistics.percentage;
  const answered = game.round.status === 'answered';
  const completed = game.status === 'completed';
  const soloCompleted = completed && game.mode !== 'friend';
  const waitingForFriendFinish = game.mode === 'friend' && completed && game.friendTable?.status === 'active';
  const ended = completed || game.status === 'cancelled';
  if (answered || ended) element('joker-letter-dialog').close();
  element('game-completion').hidden = !soloCompleted;
  element('summary-new-game').hidden = !soloCompleted;
  element('final-maximum-points').textContent = formatNumber(game.statistics.maximumPossiblePoints);
  element('final-percentage-progress').value = game.statistics.percentage;
  const summaryTitle = element('round-summary-title');
  summaryTitle.dataset.i18n = soloCompleted ? 'gameCompleted' : 'roundSummary';
  summaryTitle.textContent = translate(summaryTitle.dataset.i18n);
  const continueLabel = element('continue-game').querySelector('[data-i18n]');
  continueLabel.dataset.i18n = waitingForFriendFinish ? 'waitingFriendResult' : ended ? 'viewFinalResults' : 'continueGame';
  continueLabel.textContent = translate(continueLabel.dataset.i18n);
  document.body.dataset.roundState = answered ? game.round.result.isBest ? 'best' : game.round.result.code : 'active';
  element('submit-answer').hidden = answered || ended;
  element('next-round').hidden = !answered || ended;
  element('round-result').hidden = !answered;
  element('solutions-card').hidden = !answered || Boolean(game.round.waitingForFriend);
  element('suggestion-panel').hidden = !game.round.result?.canSuggestWord;
  element('word-input').lang = game.language.code;
  element('word-input').dir = game.language.direction;
  if (renderedRoundId !== game.round.id || renderedBoardRevision !== game.round.boardRevision) {
    element('word-input').value = '';
    selectedTileIds = new Set();
    jokerChoices.clear();
    element('joker-letter-dialog').close();
    renderedRoundId = game.round.id;
    renderedBoardRevision = game.round.boardRevision;
    assistanceContext = undefined;
    element('assistance-dialog').close();
    renderAccessibleTiles();
  }
  if (answered) {
    const result = game.round.result;
    element('word-input').value = result.answer;
    element('result-title').textContent = translate(result.isBest ? 'best' : result.code);
    element('result-title').title = element('result-title').textContent;
    element('result-points').textContent = translate('resultPoints', { points: formatNumber(result.points), maximum: formatNumber(result.maximumPoints) });
    element('round-result').classList.toggle('success', result.code === 'correct');
    element('round-result').classList.toggle('best-result', result.isBest);
    element('result-icon').replaceChildren(createIcon(result.isBest ? 'crown' : result.code === 'correct' ? 'check' : 'warning'));
  }
  element('solutions-list').replaceChildren();
  if (reportRoundId !== game.round.id) {
    selectedReportWords.clear();
    reportRoundId = game.round.id;
    setSolutionsExpanded(false);
  }
  element('solutions-count').textContent = formatNumber(game.round.solutions?.length ?? 0);
  for (const solution of game.round.solutions ?? []) {
    const item = document.createElement('li');
    item.dataset.word = solution.word;
    item.classList.toggle('player-answer', solution.word === game.round.result.normalizedAnswer);
    const word = document.createElement('label');
    word.className = 'solution-word';
    word.lang = game.language.code;
    word.dir = game.language.direction;
    word.textContent = solution.word;
    const points = document.createElement('strong');
    points.textContent = formatNumber(solution.points);
    item.append(word, points);
    if (solution.points === game.round.result.maximumPoints) item.appendChild(createIcon('crown'));
    const reported = game.round.reportedWords?.includes(solution.word) ?? false;
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'solution-checkbox';
    checkbox.value = solution.word;
    checkbox.dataset.reported = String(reported);
    checkbox.checked = reported || selectedReportWords.has(solution.word);
    checkbox.setAttribute('aria-label', translate(reported ? 'wordReportedLabel' : 'reportWordLabel', { word: solution.word }));
    checkbox.addEventListener('change', () => {
      if (checkbox.checked) selectedReportWords.add(solution.word);
      else selectedReportWords.delete(solution.word);
      synchronizeControls();
    });
    word.prepend(checkbox);
    if (reported) {
      const badge = document.createElement('small');
      badge.className = 'solution-reported';
      badge.textContent = translate('wordReported');
      item.appendChild(badge);
    }
    element('solutions-list').appendChild(item);
  }
  applyAdminSolutionFilters();
  letterBoard?.renderTiles(game.round.tiles, game.language.code, answered);
  updateSelection();
  synchronizeResultPopup((answered || ended) && presentedResultRoundId !== game.round.id);
  synchronizeFriendDialogs(game.friendTable);
  updatePlayerActions(game);
  if (newlyAnswered && ['not_in_dictionary', 'unavailable_letters', 'timed_out'].includes(game.round.result?.code)) playIncorrectWordSound();
}

function closeDialogs() { for (const dialog of document.querySelectorAll('dialog[open]')) dialog.close(); }
function openDialog(id) {
  closeDialogs();
  element(id).showModal();
}

async function startGame(languageCode, mode = 'practice') {
  await leaveFriendTable();
  closeDialogs();
  friendTableId = undefined;
  const url = new URL(location.href);
  url.searchParams.delete('table');
  history.replaceState(null, '', url);
  renderGame(await requestApi('/games', { method: 'POST', body: { languageCode, mode, jokerEnabled: mode === 'practice' && jokerEnabled } }));
  if (currentGame.status === 'completed') synchronizeResultPopup(true);
  window.scrollTo(0, 0);
}

function renderPreviewBoard() {
  const isFrench = getLocale() === 'fr';
  const isSpanish = getLocale() === 'es';
  const isEnglish = getLocale() === 'en';
  const isRussian = getLocale() === 'ru';
  const symbols = isFrench ? ['M','O','T','S','J','O','U','E','R'] : isSpanish ? ['P','A','L','A','B','R','A','Ñ','O'] : isRussian ? ['С','Л','О','В','А','Р','И','Г','Р'] : isEnglish ? ['W','O','R','D','C','R','A','F','T'] : ['ვ','ა','რ','გ','ი','ა','დ','ო','მ'];
  const points = isFrench ? symbols.map(symbol => frenchRules.letters.find(letter => letter.symbol === symbol.toLocaleLowerCase('fr')).points) : isSpanish ? symbols.map(symbol => spanishRules.letters.find(letter => letter.symbol === symbol.toLocaleLowerCase('es')).points) : isRussian ? symbols.map(symbol => russianRules.letters.find(letter => letter.symbol === symbol.toLocaleLowerCase('ru')).points)
    : isEnglish ? [4,1,1,2,3,1,1,4,1] : [4,1,2,3,1,1,3,1,2];
  const previewTiles = document.querySelectorAll('.preview-tiles > span');
  previewTiles.forEach((tile, index) => { const score = document.createElement('small'); score.textContent = String(points[index]); tile.replaceChildren(document.createTextNode(symbols[index]), score); });
}

element('new-game').addEventListener('click', () => hasLanguageGame() ? openDialog('new-game-dialog') : performAction(() => startGame(getLocale())));
element('confirm-new-game').addEventListener('click', () => performAction(() => startGame(getLocale())));
element('continue-game').addEventListener('click', () => performAction(resumeLanguageGame));
element('resume-game').addEventListener('click', () => performAction(resumeLanguageGame));
element('show-home').addEventListener('click', () => performAction(async () => {
  await leaveFriendTable();
  showHome();
}));
element('menu-new-game').addEventListener('click', () => openDialog('new-game-dialog'));
element('answer-form').addEventListener('submit', event => {
  event.preventDefault();
  if (!currentGame || currentGame.status !== 'active' || currentGame.round.status !== 'active') return;
  const gameId = currentGame.id;
  const roundId = currentGame.round.id;
  const body = { answer: element('word-input').value, boardRevision: currentGame.round.boardRevision, tileIds: currentSelection.selectedTiles.map(tile => tile.id) };
  performAction(async () => {
    const game = await requestApi(`/games/${gameId}/rounds/${roundId}/answer`, { method: 'POST', body });
    renderGame(game);
    if (game.round.status === 'answered' && game.round.result?.code === 'correct') playCorrectWordSound();
  });
});
element('next-round').addEventListener('click', () => performAction(async () => {
  const gameId = currentGame.id;
  const previousRoundId = currentGame.round.id;
  const game = await requestApi(`/games/${gameId}/rounds/${previousRoundId}/next`, { method: 'POST', body: {} });
  renderGame(game);
  if (game.id === gameId && game.round.id !== previousRoundId) playNextRoundSound();
}));
element('suggest-word').addEventListener('click', () => performAction(async () => {
  await requestApi(`/games/${currentGame.id}/rounds/${currentGame.round.id}/word-suggestions`, { method: 'POST', body: {} });
  renderGame(await requestApi(`/games/${currentGame.id}`));
}));
element('word-input').addEventListener('input', event => { if (!event.isComposing) updateSelection(true); });
element('word-input').addEventListener('compositionend', () => updateSelection(true));
element('clear-word').addEventListener('click', () => { element('word-input').value = ''; selectedTileIds = new Set(); jokerChoices.clear(); updateSelection(true); });
element('backspace-word').addEventListener('click', removeLastTile);
element('focus-keyboard').addEventListener('click', () => {
  if (mobileViewport.matches) return;
  element('word-input').focus();
});
for (const trigger of document.querySelectorAll('[data-open-dialog]')) trigger.addEventListener('click', () => openDialog(trigger.dataset.openDialog));
for (const trigger of document.querySelectorAll('[data-close-dialog]')) trigger.addEventListener('click', closeDialogs);
for (const dialog of document.querySelectorAll('dialog')) dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); });
for (const flag of document.querySelectorAll('[data-locale]')) flag.addEventListener('click', () => performAction(async () => {
  await leaveFriendTable();
  setLocale(flag.dataset.locale);
  currentGame = undefined;
  updatePlayerActions();
  showHome();
  renderPreviewBoard();
  try {
    const gameId = savedGames.get(getLocale());
    if (gameId) await restoreLanguageGame(gameId);
  } finally { showHome(); }
}));
element('round-assistance-toggle').addEventListener('change', event => {
  roundAssistanceEnabled = event.target.checked;
  localStorage.setItem('wordcraft.roundAssistance', String(roundAssistanceEnabled));
  if (currentGame) renderAccessibleTiles();
  synchronizeControls();
});

applyThemeAssets();
setupPlayerUi({ openDialog, getGame: () => hasLanguageGame() ? currentGame : undefined,
  playDaily: () => performAction(() => startGame(getLocale(), 'daily')) });
applyIcons();
setLocale(getLocale());
setupBackgroundMusic();
synchronizeMobileWordControls();
element('round-assistance-toggle').checked = roundAssistanceEnabled;
await performAction(async () => {
  isAdmin = (await initializeSession()).isAdmin;
  const player = await refreshPlayer();
  assistanceMode = (await requestApi('/game-features')).assistance;
  availableLanguages = await requestApi('/languages');
  const savedGameId = savedGames.get(getLocale()) ?? savedGames.legacy() ?? player.latestGameId;
  if (savedGameId) await restoreLanguageGame(savedGameId);
  showHome();
  renderPreviewBoard();
  if (new URLSearchParams(location.search).get('account') === 'delete') await openPlayerProfile();
  const tableId = new URLSearchParams(location.search).get('table');
  if (tableId) enterFriendTable(await requestApi(`/friend-tables/${encodeURIComponent(tableId)}/join`, { method: 'POST', body: {} }));
});
try {
  letterBoard = new LetterBoard(element('letter-board'), selectTile, tile => openAssistance('letter', tile));
  await letterBoard.initialize();
  element('accessible-tiles').classList.add('accessible-tile-buttons');
  if (currentGame) letterBoard.renderTiles(currentGame.round.tiles, currentGame.language.code, currentGame.round.status === 'answered');
  letterBoard.setSelectedTiles(selectedTileIds);
  synchronizeControls();
} catch (error) {
  console.warn('PixiJS unavailable; using accessible HTML letter tiles.', error);
  letterBoard?.destroy();
  letterBoard = undefined;
  element('letter-board').hidden = true;
}
